/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from "react"
import type { RefObject } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"
import { useEffectOnce, useIsomorphicLayoutEffect, useUnmount } from "react-use"

import { captureCoachingChatSocketErrorWithSentry, minutes } from "@/3514/utils"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import {
    type TCoachingChatCreateSocketTokenMutation as TSocketTokenMutation,
    type TCoachingChatCreateSocketTokenMutationResponse as TSocketTokenMutationResponse,
    coachingChatSocket as socket,
    useCoachingChatSocketTokenMutation as useSocketTokenMutation
} from "../api"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"
import { coachingChatApiUtils as apiUtils } from "../utils"

const {
    socket: { setToken, isTokenValid }
} = apiUtils

const REST_FALLBACK_FAILED_ATTEMPTS: number = 3

type _TParams = _TSharedParams & {
    isChatEnabled: boolean
    existingConversationId: number
}

function useCoachingChatConnectionManager({
    entityId,
    entityMode,
    wasChatCompleted,
    isChatEnabled,
    existingConversationId
}: _TParams): void {
    const isFullMode: boolean = eq(entityMode, EEntityMode.Full)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const { mutateAsync, reset: resetMutation }: TSocketTokenMutation = useSocketTokenMutation({
        onSuccess: async (resp: TSocketTokenMutationResponse): Promise<void> => {
            setToken(resp.data?.token)

            socket.close()

            socket.auth = { token: resp.data.token }

            socket.connect()
        },
        onError: async (): Promise<void> => {
            socket.disconnect()

            socket.close()

            setToken(String(""))
        }
    })

    useIsomorphicLayoutEffect((): void => {
        if (!isChatEnabled || wasChatCompleted || !isFullMode || !JSAPP_CONF?.websockets_enabled) return void 0

        if (isTokenValid()) return void socket.connect()

        return void mutateAsync(void 0)
    }, [isChatEnabled, wasChatCompleted, isFullMode])

    const hasEverConnectedRef: RefObject<boolean> = useRef<boolean>(socket.connected)

    const failedReconnectsRef: RefObject<number> = useRef<number>(0)

    useEffect((): TEmptyCallback => {
        if (!isChatEnabled || wasChatCompleted || !isFullMode || !JSAPP_CONF?.websockets_enabled) return emptyCallback

        function connector(): void {
            hasEverConnectedRef.current = true
            failedReconnectsRef.current = 0

            setConversationApiMode(EConversationApiMode.Sockets)
            if (isNumber(existingConversationId)) conversationDispatcher.changeStatus(EConversationStatus.Connected)
        }

        function disconnector(): void {
            setConversationApiMode(EConversationApiMode.Rest)
        }

        function errorConnector(err: Error): void {
            captureCoachingChatSocketErrorWithSentry(err)

            if (!hasEverConnectedRef.current) {
                setConversationApiMode(EConversationApiMode.Rest)
                return
            }

            failedReconnectsRef.current += 1

            if (failedReconnectsRef.current >= REST_FALLBACK_FAILED_ATTEMPTS)
                setConversationApiMode(EConversationApiMode.Rest)
        }

        socket.on("connect", connector)
        socket.on("disconnect", disconnector)
        socket.on("connect_error", errorConnector)

        return (): void => {
            socket.off("connect", connector)
            socket.off("disconnect", disconnector)
            socket.off("connect_error", errorConnector)
        }
    }, [isChatEnabled, wasChatCompleted, existingConversationId, isFullMode])

    const checkConnection: TEmptyCallback = useCallback((): void => {
        if (
            !isChatEnabled ||
            wasChatCompleted ||
            !isFullMode ||
            !JSAPP_CONF?.websockets_enabled ||
            includes<EConversationStatus>(
                [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
                conversationStatus
            )
        )
            return void 0

        if (isTokenValid() || !socket.connected) return void 0

        void mutateAsync(void 0)

        return void 0
    }, [isChatEnabled, wasChatCompleted, isFullMode, conversationStatus, mutateAsync])

    useIsomorphicLayoutEffect((): TEmptyCallback => {
        const int: ReturnType<typeof setInterval> = setInterval((): void => void checkConnection(), minutes(2))

        return (): void => void clearTimeout(int)
    }, [checkConnection])

    useEffectOnce((): void => {
        if (!isFullMode || wasChatCompleted || !isChatEnabled || !JSAPP_CONF?.websockets_enabled)
            setConversationApiMode(EConversationApiMode.Rest)
    })

    useUnmount((): void => {
        if (isChatEnabled) resetMutation()

        if (!wasChatCompleted && isFullMode) {
            socket.disconnect()
            socket.close()
        }
    })
}

export { useCoachingChatConnectionManager }
